import { H2, Section, useDynamicConfig, useOpen } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router";
import styled from "styled-components";
import { CreateEventProvider } from "../../../shared/events/save/CreateEventProvider";
import { UpdateEventTypePage } from "../../../shared/events/save/UpdateEventTypePage";
import { FilterProvider } from "../../../shared/filters";
import { eventService } from "../shared/api/eventService";
import { EventCalendarPage } from "./calendar/EventCalendarPage";
import { CreateEventPage } from "./CreateEventPage";
import { EventDetailsPage } from "./details/EventDetailsPage";
import { DuplicateEventPage } from "./DuplicateEventPage";
import { EventOverviewPage } from "./overview/EventOverviewPage";
import { UpdateEventPage } from "./UpdateEventPage";

const Wrapper = styled(Section)`
	padding-top: ${({ theme }) => theme.spacing(4)};
	padding-bottom: ${({ theme }) => theme.spacing(4)};
	display: grid;
	gap: ${({ theme }) => theme.spacing(4)};
`;

const Container = styled.div`
	overflow: hidden;
	display: grid;
	gap: ${({ theme }) => theme.spacing(4)};
`;

export interface EventScopeProps {
	showOnlyOwnEvents: boolean;
	onToggle: () => void;
}

export const EventPageLayout: FC = () => {
	const { t } = useTranslation();

	return (
		<Wrapper color="content">
			<H2>{t("Events")}</H2>
			<Container>
				<Outlet />
			</Container>
		</Wrapper>
	);
};

export const EventsPage: FC = () => {
	const { defaultCurrency } = useDynamicConfig();
	const { open: showOnlyOwnEvents, handleOpen, handleClose } = useOpen();
	const props: EventScopeProps = {
		showOnlyOwnEvents: showOnlyOwnEvents,
		onToggle: () => (showOnlyOwnEvents ? handleClose() : handleOpen()),
	};

	return (
		<FilterProvider entity="EVENT" keysToInvalidate={[eventService.listOverviewKey]}>
			<CreateEventProvider defaultCurrency={defaultCurrency}>
				<Routes>
					<Route index element={<Navigate to="calendar" replace={true} />} />
					<Route path="*" element={<EventPageLayout />}>
						<Route path="calendar" element={<EventCalendarPage {...props} />} />
						<Route path="overview" element={<EventOverviewPage {...props} />} />
					</Route>
					<Route path="/:viewType/:eventId/*" element={<EventDetailsPage />} />
					<Route path="/:viewType/update/:eventId">
						<Route
							path="*"
							element={
								<Wrapper color="content">
									<UpdateEventPage />
								</Wrapper>
							}
						/>
					</Route>
					<Route path="/:viewType/:eventId/update-event-type/*" element={<UpdateEventTypePages />} />
					<Route
						path="/:viewType/create/*"
						element={
							<Wrapper color="content">
								<CreateEventPage />
							</Wrapper>
						}
					/>
					<Route
						path="/:viewType/create/from-event/:eventId/*"
						element={
							<Wrapper color="content">
								<DuplicateEventPage />
							</Wrapper>
						}
					/>
				</Routes>
			</CreateEventProvider>
		</FilterProvider>
	);
};

export const UpdateEventTypePages: FC = () => {
	const { viewType = "" } = useParams<"viewType">();

	return (
		<Wrapper color="content">
			<UpdateEventTypePage basePath={`/events/${viewType}`} keysToInvalidate={[eventService.baseKey]} />
		</Wrapper>
	);
};
